/* YCODE price test engine. 20.10.26.

   WHAT THIS DOES
   Half of eligible new visitors are shown tier B prices and charged tier B
   prices. The charge is made correct by swapping the variant id, and the
   selling plan id, on their way to Shopify. Nothing is "made to look" cheaper.

   TWO SWAPS, NOT ONE
   Tier B products are separate Shopify products, so they have their own
   variant ids AND their own Recharge selling plan ids. Swapping only the
   variant sends tier A's plan id with tier B's variant, which Shopify refuses,
   and the add to bag fails silently for anyone choosing a subscription. Both
   maps are therefore required and both are applied together.

   WHY THE SWAP HAPPENS AT THE NETWORK LAYER
   The theme adds to bag from ten different places (the PDP form, eight
   quick-add buttons, the drawer swap, the landing pages, search, the formula
   finder). Patching each would leave gaps and rot the first time an eleventh
   appears. Wrapping fetch, XHR and form submit catches all of them at once.

   WHY THE DISPLAY IS DERIVED, NOT ANNOTATED
   Every surface that shows one of these prices already carries the variant id
   in its markup: the PDP hidden input, each card's quick-add button. So the
   engine finds its own targets rather than needing a data attribute added at
   every render point in the theme. Fewer edits, fewer places to forget.

   WHAT IS DELIBERATELY NOT TOUCHED
   Bag, drawer and checkout prices. Once the line holds a tier B variant,
   Shopify reports its real price and those surfaces are already correct.
   Rewriting them would risk display and charge disagreeing, which is the one
   failure that must never happen.

   SAFETY
   window.YC_PT.enabled is false unless the shop metafield says otherwise, so
   this file is inert until the test is switched on at the source.
*/
(function () {
  'use strict';

  var CFG = window.YC_PT;
  if (!CFG || !CFG.enabled) return;

  var DOC = document.documentElement;
  var A = 'A', B = 'B';

  /* ---------------------------------------------------------------- storage */
  /* Guarded throughout. In a private window, with site data blocked, or inside
     a preview frame these throw rather than return null, and an exception here
     would take the page down with it. */
  function cookieGet(name) {
    try {
      var parts = ('; ' + document.cookie).split('; ' + name + '=');
      return parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : null;
    } catch (e) { return null; }
  }
  function cookieSet(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax' +
        (location.protocol === 'https:' ? ';Secure' : '');
      return document.cookie.indexOf(name + '=') !== -1;
    } catch (e) { return false; }
  }
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); return true; } catch (e) { return false; } }

  /* ------------------------------------------------------------- assignment */
  /* durable=false means the choice could not be persisted. Such a visitor is
     put in A and marked excluded: showing a price we cannot hold would mean the
     number changing under them on the next page, which is worse than not
     testing them at all. */
  function resolveGroup() {
    var key = CFG.cookie;

    /* The head snippet decides and persists before anything in the body runs,
       so its answer is the answer. Trusting it rather than repeating the logic
       is deliberate: when both copies existed they drifted, and the drift was
       not visible. A dry run (split 0) wrote cookies from here and, because
       `CFG.split || 0.5` turns 0 into 0.5 in JavaScript, would have put half of
       all visitors into the test without anyone deciding to start it. */
    if (CFG.group === A || CFG.group === B) {
      /* CFG.excluded carries the head snippet's reason for standing a visitor
         out of the test, so it has to survive into STATE and onto the order.
         Without it an existing customer is indistinguishable from a control
         group visitor in the data. */
      return {
        group: CFG.group,
        assigned: false,
        durable: CFG.durable !== false,
        excluded: CFG.excluded || null,
        /* How the visitor got this arm: 'link' for the Meta campaign token,
           'legacy' for a cookie left over from the randomised run, 'random'
           only if a controlled test is restarted from the metafield. Carried
           onto the order so the three can never be pooled by accident. */
        source: CFG.source || null
      };
    }

    /* Fallback for the case where this file somehow runs without the snippet
       having set a group. Kept so the engine is not silently inert, and
       written to match the snippet exactly. */
    var stored = cookieGet(key) || lsGet(key);
    if (stored === A || stored === B) {
      cookieSet(key, stored, CFG.days);   /* slide the window forward */
      lsSet(key, stored);
      return { group: stored, assigned: false, durable: true };
    }
    var split = typeof CFG.split === 'number' ? CFG.split : 0.5;
    if (!CFG.eligible) return { group: A, durable: true, excluded: 'ineligible' };
    if (split <= 0) return { group: A, durable: true, excluded: 'not-running' };

    var group = Math.random() < split ? B : A;
    var ok = cookieSet(key, group, CFG.days);
    var ok2 = lsSet(key, group);
    if (!ok && !ok2) return { group: A, durable: false, excluded: 'no-storage' };
    return { group: group, assigned: true, durable: true };
  }

  var STATE = resolveGroup();
  DOC.setAttribute('data-yc-px', STATE.group);
  var ACTIVE = STATE.group === B;

  /* --------------------------------------------------------------- the maps */
  var VAR = CFG.variants || {};   /* tier A variant id -> {b, sku, A:{cur}, B:{cur}} */
  var PLAN = CFG.plans || {};     /* tier A plan id    -> {b, pct}                   */
  var HANDLES = CFG.handles || {};/* tier B handle     -> canonical handle           */
  var CUR = CFG.currency;

  function toBVariant(id) { var e = VAR[String(id)]; return e ? String(e.b) : null; }
  function toBPlan(id) { var e = PLAN[String(id)]; return e ? String(e.b) : null; }
  function listPrice(variantId, group) {
    var e = VAR[String(variantId)];
    if (!e) return null;
    var set = e[group === B ? 'B' : 'A'];
    return set ? set[CUR] : null;
  }
  /* A subscription price is the tier B list price less that plan's own
     percentage. The percentages differ per product, by design, so that every
     GBP subscription price lands on a whole pound. */
  function planPrice(variantId, aPlanId) {
    var base = listPrice(variantId, B);
    var p = PLAN[String(aPlanId)];
    if (base == null || !p) return null;
    return Math.round(base * (1 - p.pct / 100));
  }

  /* ------------------------------------------------- rewriting cart payloads */
  function swapObj(o) {
    if (!o || typeof o !== 'object') return o;
    if (Array.isArray(o)) { o.forEach(swapObj); return o; }
    if (Array.isArray(o.items)) o.items.forEach(swapObj);
    if (o.id != null) { var b = toBVariant(o.id); if (b) o.id = Number(b); }
    if (o.selling_plan != null) { var sp = toBPlan(o.selling_plan); if (sp) o.selling_plan = Number(sp); }
    if (o.updates && typeof o.updates === 'object') {
      var out = {};
      for (var k in o.updates) if (o.updates.hasOwnProperty(k)) {
        var nb = toBVariant(k);
        out[nb || k] = o.updates[k];
      }
      o.updates = out;
    }
    return o;
  }

  function swapForm(body) {
    var cur = body.get('id');
    if (cur) { var b = toBVariant(cur); if (b) body.set('id', b); }
    var sp = body.get('selling_plan');
    if (sp) { var p = toBPlan(sp); if (p) body.set('selling_plan', p); }
    return body;
  }

  function swapBody(body) {
    if (!ACTIVE || body == null) return body;
    try {
      if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{' || t.charAt(0) === '[') return JSON.stringify(swapObj(JSON.parse(t)));
        /* Built with RegExp rather than literals: this file is sometimes moved
           through JSON tooling, where a backslash in a regex literal is one
           mis-escape away from becoming a syntax error that only shows at
           runtime. Nothing here needs a backslash, so it does not have one. */
        return body
          .replace(new RegExp('(^|&)id=([0-9]+)', 'g'), function (m, pre, id) { var b = toBVariant(id); return b ? pre + 'id=' + b : m; })
          .replace(new RegExp('(^|&)selling_plan=([0-9]+)', 'g'), function (m, pre, id) { var b = toBPlan(id); return b ? pre + 'selling_plan=' + b : m; });
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) return swapForm(body);
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return swapForm(body);
      if (typeof body === 'object') return swapObj(body);
    } catch (e) { /* malformed payload: leave exactly as it was */ }
    return body;
  }

  function isCartWrite(url){
    if (!url) return false;
    url = String(url);
    return url.indexOf('/cart/add') >= 0 || url.indexOf('/cart/update') >= 0 || url.indexOf('/cart/change') >= 0;
  }

  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (ACTIVE && isCartWrite(url) && init && init.body) {
          init = Object.assign({}, init, { body: swapBody(init.body) });
        }
      } catch (e) { /* never block the request */ }
      return _fetch.call(this, input, init);
    };
  }

  var _open = XMLHttpRequest.prototype.open, _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, u) { this.__ycUrl = u; return _open.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function (body) {
    try { if (ACTIVE && isCartWrite(this.__ycUrl)) arguments[0] = swapBody(body); } catch (e) {}
    return _send.apply(this, arguments);
  };

  /* A native form POST to /cart/add never passes through fetch or XHR. */
  document.addEventListener('submit', function (ev) {
    if (!ACTIVE) return;
    var f = ev.target;
    if (!f || !f.action || String(f.action).indexOf('/cart/add') < 0) return;
    try {
      var idF = f.querySelector('[name="id"]');
      if (idF && idF.value) { var b = toBVariant(idF.value); if (b) idF.value = b; }
      var spF = f.querySelector('[name="selling_plan"]');
      if (spF && spF.value) { var p = toBPlan(spF.value); if (p) spF.value = p; }
    } catch (e) { /* let the form submit as it stands */ }
  }, true);

  /* ------------------------------------------------------------- formatting */
  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: CUR,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      return (CUR === 'GBP' ? '£' : '$') + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
    }
  }
  function setText(el, cents) { if (el && cents != null) el.textContent = money(cents); }

  /* --------------------------------------------------------------- display */
  /* Targets are found from the variant ids the theme already renders. */
  function paintPDP(root) {
    var idField = (root || document).querySelector('[data-yc-variant-id]');
    if (!idField) return;
    var vid = idField.value;
    var listB = listPrice(vid, B);
    if (listB == null) return;                       /* not a tested product */

    setText((root || document).querySelector('[data-yc-price]'), listB);
    setText((root || document).querySelector('[data-yc-sticky-onetime]'), listB);
    setText((root || document).querySelector('[data-yc-sticky-subwas]'), listB);

    /* The headline subscribe row and the sticky bar both show the first plan,
       which the theme renders in selling-plan-group order. Read the chosen
       plan from the select when it exists so the two cannot disagree. */
    var sel = (root || document).querySelector('[data-yc-plan-select]');
    var firstPlan = sel && sel.options.length ? sel.options[sel.selectedIndex >= 0 ? sel.selectedIndex : 0].value : null;
    var subB = firstPlan ? planPrice(vid, firstPlan) : null;
    setText((root || document).querySelector('[data-yc-sub-now]'), subB);
    setText((root || document).querySelector('[data-yc-sticky-subnow]'), subB);

    var saveEl = (root || document).querySelector('[data-yc-sub-save]');
    if (saveEl && subB != null) saveEl.textContent = 'SAVE ' + money(listB - subB);

    /* Repoint each option at the tier B price, and rewrite the price map the
       theme's own variant-switch code reads, so a formula change repaints with
       tier B numbers without this engine having to re-run. */
    if (sel) {
      for (var i = 0; i < sel.options.length; i++) {
        var o = sel.options[i], pp = planPrice(vid, o.value);
        if (pp == null) continue;
        var label = (o.textContent || '').split(',')[0];
        o.setAttribute('data-price', money(pp));
        o.setAttribute('data-save', money(listB - pp));
        o.textContent = label + ', ' + money(pp) + ' (Save ' + money(listB - pp) + ')';
      }
    }
    var mapEl = (root || document).querySelector('[data-yc-prices]');
    if (mapEl) {
      try {
        var map = JSON.parse(mapEl.textContent || '{}'), out = {};
        for (var k in map) if (map.hasOwnProperty(k)) {
          var lb = listPrice(k, B);
          out[k] = lb == null ? map[k] : { price: money(lb), sub: money(planPrice(k, firstPlan) != null ? planPrice(k, firstPlan) : lb) };
        }
        mapEl.textContent = JSON.stringify(out);
      } catch (e) { /* leave the map alone rather than corrupt it */ }
    }
  }

  /* Any card, tile or quick-add block that carries its own add-to-bag form also
     carries its variant id in that form, so the price beside it can be found and
     corrected without the theme having to annotate every layout. The shop grid,
     the product cards and the final-CTA strip all differ in markup and all work
     through this one rule.

     The product page is deliberately excluded: its form holds the marker
     data-yc-variant-id and is handled by paintPDP, which also has the
     subscription rows and the plan dropdown to deal with. */
  var PRICE_IN_CARD = '.ycshop-tile__price, .yc-pcard__price, .wt-product__price__final, .card__price, [data-yc-pt-price]';

  function paintCards(root) {
    var forms = (root || document).querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (f.getAttribute('data-yc-pt-done') === '1') continue;
      if (f.querySelector('[data-yc-variant-id]')) { f.setAttribute('data-yc-pt-done', '1'); continue; }
      var idEl = f.querySelector('[name="id"]');
      var vid = idEl && idEl.value;
      if (!vid) { f.setAttribute('data-yc-pt-done', '1'); continue; }
      var lb = listPrice(vid, B);
      if (lb == null) { f.setAttribute('data-yc-pt-done', '1'); continue; }

      /* Climb only as far as the nearest ancestor that actually holds a price,
         so a grid wrapper can never have every tile's price overwritten with
         one product's figure. */
      var node = f.parentElement, target = null, hops = 0;
      while (node && hops < 5) {
        var found = node.querySelectorAll(PRICE_IN_CARD);
        if (found.length === 1) { target = found[0]; break; }
        if (found.length > 1) break;
        node = node.parentElement; hops++;
      }
      if (target) setText(target, lb);
      f.setAttribute('data-yc-pt-done', '1');
    }
    /* Quick-add buttons that sit outside a form still carry the id directly. */
    var adds = (root || document).querySelectorAll('[data-yc-quickadd][data-variant-id]');
    for (var j = 0; j < adds.length; j++) {
      var btn = adds[j];
      if (btn.getAttribute('data-yc-pt-done') === '1') continue;
      var blb = listPrice(btn.getAttribute('data-variant-id'), B);
      if (blb != null) {
        var card = btn.closest('.yc-pcard, .ycshop-tile, article, li') || btn.parentElement;
        var pe = card && card.querySelector(PRICE_IN_CARD);
        setText(pe, blb);
      }
      btn.setAttribute('data-yc-pt-done', '1');
    }
  }

  function paintExplicit(root) {
    var els = (root || document).querySelectorAll('[data-yc-pt-price]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute('data-yc-pt-done') === '1') continue;
      setText(el, listPrice(el.getAttribute('data-yc-pt-price'), B));
      el.setAttribute('data-yc-pt-done', '1');
    }
    /* data-yc-pt-planprice="<variantId>:<planId>" is the subscription twin of the
       above: the figure shown is a plan price, not the list price. */
    var pls = (root || document).querySelectorAll('[data-yc-pt-planprice]');
    for (var j = 0; j < pls.length; j++) {
      var pe = pls[j];
      if (pe.getAttribute('data-yc-pt-done') === '1') continue;
      var bits = String(pe.getAttribute('data-yc-pt-planprice')).split(':');
      if (bits.length === 2) setText(pe, planPrice(bits[0], bits[1]));
      pe.setAttribute('data-yc-pt-done', '1');
    }
    /* The two derived figures on a bundle page: what the parts cost bought
       separately, and the saving against it. Neither is any one variant's
       price, so neither can use the rules above. Left alone they keep the
       tier A arithmetic, which in group B overstates the saving badly: the
       Advanced Routine would read "was £152, now £109" when the honest
       comparison is £123 against £109. That is a price claim, and it would
       have flattered exactly the arm the test is measuring. */
    var sums = (root || document).querySelectorAll('[data-yc-pt-sum]');
    for (var k = 0; k < sums.length; k++) {
      var se = sums[k];
      if (se.getAttribute('data-yc-pt-done') === '1') continue;
      setText(se, sumOf(se.getAttribute('data-yc-pt-sum')));
      se.setAttribute('data-yc-pt-done', '1');
    }
    /* data-yc-pt-saving="<bundleVariantId>|<componentId,componentId,...>" */
    var savs = (root || document).querySelectorAll('[data-yc-pt-saving]');
    for (var m = 0; m < savs.length; m++) {
      var ve = savs[m];
      if (ve.getAttribute('data-yc-pt-done') === '1') continue;
      var parts = String(ve.getAttribute('data-yc-pt-saving')).split('|');
      if (parts.length === 2) {
        var whole = listPrice(parts[0], B);
        var tot = sumOf(parts[1]);
        if (whole != null && tot != null && tot > whole) {
          ve.textContent = (ve.getAttribute('data-yc-pt-prefix') || '')
            + money(tot - whole).replace('.00', '')
            + (ve.getAttribute('data-yc-pt-suffix') || '');
        }
      }
      ve.setAttribute('data-yc-pt-done', '1');
    }
  }

  /* Sum of the tier B list prices of a comma-separated list of tier A variant
     ids. One unrecognised component makes the whole total wrong rather than
     slightly wrong, so the answer in that case is null and the authored figure
     is left standing. */
  function sumOf(csv) {
    if (!csv) return null;
    var ids = String(csv).split(','), total = 0;
    for (var i = 0; i < ids.length; i++) {
      var id = ids[i].replace(/\s/g, '');
      if (!id) continue;
      var p = listPrice(id, B);
      if (p == null) return null;
      total += p;
    }
    return total > 0 ? total : null;
  }

  /* ------------------------------------------------- JSON-fed sections
     The kit picker and the formula finder hand their prices to JavaScript as
     a JSON script tag. Correcting those is NOT done here, because it cannot
     be: the formula finder parses its own JSON in an inline script inside the
     section, and nothing loaded as a file runs before that, deferred or not.
     The patching therefore lives inline in the head, in
     snippets/yc-price-test.liquid, exposed as window.YC_PT.patch(cfg, kind),
     and the two consumers call it on the object they just parsed. See the
     comment at the top of that snippet. */

  /* A tier B product is unlisted, so a link built from a bag line's handle
     would be a dead end. Point those at the real product page. */
  function fixLinks(root) {
    var as = (root || document).querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < as.length; i++) {
      var h = as[i].getAttribute('href') || '';
      for (var bh in HANDLES) if (HANDLES.hasOwnProperty(bh)) {
        if (h.indexOf('/products/' + bh) !== -1) {
          as[i].setAttribute('href', h.replace('/products/' + bh, '/products/' + HANDLES[bh]));
        }
      }
    }
  }

  function run(root) {
    if (!ACTIVE) { DOC.setAttribute('data-yc-px-ready', '1'); return; }
    try { paintPDP(root); } catch (e) {}
    try { paintCards(root); } catch (e) {}
    try { paintExplicit(root); } catch (e) {}
    try { fixLinks(root); } catch (e) {}
    DOC.setAttribute('data-yc-px-ready', '1');
  }

  /* ------------------------------------------------------------ measurement */
  var STAMPED = false;
  function stamp() {
    if (STAMPED) return;
    STAMPED = true;
    var attrs = {
      '_yc_px_group': STATE.group,
      '_yc_px_market': CFG.country || '',
      '_yc_px_v': String(CFG.v || 1)
    };
    if (STATE.excluded) attrs['_yc_px_excluded'] = STATE.excluded;
    if (STATE.source) attrs['_yc_px_src'] = STATE.source;
    try {
      window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      });
    } catch (e) { /* measurement is not worth an error */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); stamp(); });
  } else { run(); stamp(); }

  /* The drawer, quick-add and section rendering all inject markup after load.
     Coalesced so a burst of mutations does not cause a burst of repaints. */
  var pending = null;
  try {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          if (pending) return;
          pending = setTimeout(function () { pending = null; run(); }, 60);
          return;
        }
      }
    }).observe(document.body || DOC, { childList: true, subtree: true });
  } catch (e) { /* observation is an optimisation, not a requirement */ }

  /* Re-read prices when the shopper changes formula or plan. */
  document.addEventListener('change', function (ev) {
    if (!ACTIVE) return;
    var t = ev.target;
    if (t && (t.hasAttribute('data-yc-plan-select') || t.name === 'id' || t.closest('[data-yc-formula]'))) {
      setTimeout(function () { run(); }, 30);
    }
  }, true);

  /* Exposed for the quality assurance pass. Read-only by convention. */
  window.YC_PT_STATE = {
    group: STATE.group, durable: STATE.durable, excluded: STATE.excluded || null,
    source: STATE.source || null,
    active: ACTIVE, currency: CUR,
    variantPairs: Object.keys(VAR).length, planPairs: Object.keys(PLAN).length,
    toBVariant: toBVariant, toBPlan: toBPlan, listPrice: listPrice, planPrice: planPrice
  };
})();
